import Modal from '@berlitzplatforms/micro.ui.modal'
import * as React from 'react'
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl'
import { CenterText, Overrides, StyledP } from './style'
import EmptyState from '@components/EmptyState'
import Spinner from '@berlitz/spinner'
import { autoEnrollmentsByCode, CodeId } from './AutoEnrollmentCode'
import { useLazyQuery } from 'react-apollo'
import * as queryString from 'query-string'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
import WebToCase from '@components/WebToCase'
import { GetUserProfile } from '@interfaces/UserProfile'
import { GET_USER_PROFILE } from '@queries/userProfile'
import { PROFILE_TYPE } from '@utils/constants'
import { getStorageAdapter, AUTH_KEYS } from '@shell/adapters/storage'
import { getAutoEnrollmentUserContext, normalizeProductDefinitionId } from './utils'
import { isProvisioningPendingMessage } from '@utils/helpers/provisioning-pending'

interface EAPModalContentProps {
  onClose: Function
  onCancel?: Function
  onProceed?: Function
  title?: string
  subtitle?: React.ReactNode
  description?: React.ReactNode
  proceedLabel?: string
  cancelLabel?: string
  noButtons?: boolean
  anApiError?: boolean
  customButtons?: React.ReactNode
  cancelBtnType?: 'info' | 'warning' | 'error' | 'success' | 'light' | 'default' | undefined
  proceedBtnType?: 'info' | 'warning' | 'error' | 'success' | 'light' | 'default' | undefined
  cancelOutline?: boolean
  img?: string
  loading?: boolean
  hideCloseButton?: boolean
  code: string
}

interface EAPModalProps extends EAPModalContentProps {
  open: boolean
}

interface Action {
  linkUrl: string
  btnTxt: string
  onProceed?: Function
}

const USER_PROVISIONING_TIMEOUT = 'USER_PROVISIONING_TIMEOUT'
const EAP_POLLING_INTERVAL_MS = 10000
// Stop polling after 2 minutes to avoid an infinite spinner when provisioning is stuck.
const EAP_MAX_POLLING_WAIT_MS = 2 * 60 * 1000

const containsProvisioningPending = (message: unknown): boolean => isProvisioningPendingMessage(message)

const isProvisioningPendingError = (error: any): boolean => {
  if (containsProvisioningPending(error?.message)) {
    return true
  }

  if (Array.isArray(error?.graphQLErrors)) {
    return error.graphQLErrors.some((graphQLError: any) => containsProvisioningPending(graphQLError?.message))
  }

  return false
}

export const BodyText: React.FC<{ description: string }> = ({ description = '' }) => {
  return (
    description && (
      <StyledP>
        <FormattedMessage id={description} />
      </StyledP>
    )
  )
}

export const RaiseACaseModalContent: React.FC<EAPModalProps & WrappedComponentProps> = ({
  subtitle,
  description,
  onClose,
}) => {
  const { product_definition_id } = queryString.parse(window.location.search)
  const productDefinitionId =
    normalizeProductDefinitionId(product_definition_id) ??
    normalizeProductDefinitionId(getStorageAdapter().get<unknown>(AUTH_KEYS.EAP_PRODUCT_DEFINITION_ID))
  const { StudentFirstName, StudentLastName, StudentEmail } = useSelector(({ session }: StoreProps) => {
    const payload = session?.user?.signInUserSession?.idToken?.payload
    return getAutoEnrollmentUserContext(payload as Record<string, unknown> | undefined)
  })

  const extDescription = `${(subtitle as string) ?? (description as string)} | ${productDefinitionId ?? ''}`
  return (
    <>
      <WebToCase
        extEmail={StudentEmail}
        extFullname={`${StudentFirstName} ${StudentLastName}`}
        extCourseType="Online"
        extDescription={extDescription}
        onClose={onClose as () => void}
      />
    </>
  )
}
export const WarningModalContent: React.FC<EAPModalProps & WrappedComponentProps> = ({
  title,
  subtitle,
  description,
  img,
  onProceed,
  code,
}) => {
  const ActionButtonByCode = new Map<string, Action>()
  ActionButtonByCode.set('Default', {
    linkUrl: '/support',
    btnTxt: 'Raise a case',
    onProceed,
  })

  ActionButtonByCode.set(CodeId.CUSTOM_RESTRICTED_ACCESS, {} as Action)

  ActionButtonByCode.set(CodeId.ALREADY_ENROLLED_SAME_LANGUAGE, {
    linkUrl: '/',
    btnTxt: 'Go to My Berlitz',
    onProceed: () => (location.href = '/'),
  })

  ActionButtonByCode.set(CodeId.ALREADY_ENROLLED_DIFFERENT_LANGUAGE, {
    linkUrl: '/',
    btnTxt: 'Go to My Berlitz',
    onProceed: () => (location.href = '/'),
  })

  let actions = ActionButtonByCode.get(code)
  if (!actions) {
    actions = ActionButtonByCode.get('Default')!
  }

  return (
    <>
      <CenterText>
        <EmptyState
          heading={title}
          customBodyText={<BodyText description={(subtitle ?? description) as string} />}
          img={img}
          top={top}
          {...actions}
        />
      </CenterText>
    </>
  )
}

export const LoadingContent: React.FC<EAPModalProps & WrappedComponentProps> = ({
  title,
  subtitle,
  description,
  img,
}) => (
  <>
    <CenterText>
      <EmptyState heading={title} bodyTxt={subtitle ?? description} img={img} top={top}>
        <Spinner />
      </EmptyState>
    </CenterText>
  </>
)

const EAPModal: React.FC<EAPModalProps & WrappedComponentProps> = (props) => {
  const [code, setCode] = React.useState(props.code)
  const pollingStartedAtRef = React.useRef<number | null>(null)

  const [raiseCase, setRaiseCase] = React.useState(false)
  const { Code, Message, Image } =
    autoEnrollmentsByCode.get(code) ?? autoEnrollmentsByCode.get(CodeId.ENROLLMENT_ERROR)!

  const onClose = code === CodeId.ENROLLMENT_INITIALIZED || raiseCase ? () => {} : props.onClose
  const hideCloseButton = code === CodeId.ENROLLMENT_INITIALIZED || raiseCase
  const showLoaderModal = code === CodeId.ENROLLMENT_INITIALIZED

  const { product_definition_id } = queryString.parse(window.location.search)
  const productDefinitionId =
    normalizeProductDefinitionId(product_definition_id) ??
    normalizeProductDefinitionId(getStorageAdapter().get<unknown>(AUTH_KEYS.EAP_PRODUCT_DEFINITION_ID))
  const { SSOFederatedIdentityId, StudentFirstName, StudentLastName, StudentEmail, StudentExternalId } = useSelector(
    ({ session }: StoreProps) => {
      const payload = session?.user?.signInUserSession?.idToken?.payload
      return getAutoEnrollmentUserContext(payload as Record<string, unknown> | undefined)
    }
  )

  /**
   * Raise a case
   */
  const onProceed = () => {
    setRaiseCase(true)
  }

  /**
   * Auto Enrollment Mutation
   */
  let timeout: NodeJS.Timeout

  const [getUserProfile] = useLazyQuery<GetUserProfile>(GET_USER_PROFILE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      getStorageAdapter().set('test', data?.getUserProfile?.StudentProfile)
      // check if data.getUserProfile.StudentProfile has a value
      // if it does, then the user has a program created redirect to the /
      if (data?.getUserProfile?.StudentProfile) {
        const hasAnActiveRegistration =
          data?.getUserProfile?.StudentProfile?.StudentProfileInfo?.RegistrationWrappers.some(
            (registration) => registration.RegistrationInfo.Status === 'Active'
          )
        if (hasAnActiveRegistration) {
          clearInterval(timeout)
          StudentEmail && getStorageAdapter().remove(`EAP-${StudentEmail}`)
          getStorageAdapter().remove(AUTH_KEYS.EAP_PRODUCT_DEFINITION_ID)
          location.href = '/'
        }
      }
    },
    onError: (error) => {
      if (isProvisioningPendingError(error)) {
        return
      }

      const autoEnrollment = autoEnrollmentsByCode.get(CodeId.ENROLLMENT_ERROR)!
      autoEnrollmentsByCode.set(CodeId.ENROLLMENT_ERROR, {
        ...autoEnrollment,
        Code: error?.message,
        Message: autoEnrollment.Message,
      })

      setCode(CodeId.ENROLLMENT_ERROR)
      clearInterval(timeout)
    },
  })

  React.useEffect(() => {
    if (![CodeId.ENROLLMENT_INITIALIZED].includes(code as CodeId)) {
      pollingStartedAtRef.current = null
      return
    }

    pollingStartedAtRef.current = Date.now()
    timeout = setInterval(() => {
      if ([CodeId.ENROLLMENT_INITIALIZED].includes(code as CodeId)) {
        if (pollingStartedAtRef.current && Date.now() - pollingStartedAtRef.current >= EAP_MAX_POLLING_WAIT_MS) {
          const autoEnrollment = autoEnrollmentsByCode.get(CodeId.ENROLLMENT_ERROR)!
          autoEnrollmentsByCode.set(CodeId.ENROLLMENT_ERROR, {
            ...autoEnrollment,
            Code: USER_PROVISIONING_TIMEOUT,
            Message: autoEnrollment.Message,
          })

          setCode(CodeId.ENROLLMENT_ERROR)
          clearInterval(timeout)
          return
        }

        if (
          productDefinitionId &&
          SSOFederatedIdentityId &&
          StudentFirstName &&
          StudentLastName &&
          StudentEmail &&
          StudentExternalId
        ) {
          // Set StudentEmail and ProductDefinitionId to localStorage
          getStorageAdapter().set(`EAP-${StudentEmail}`, productDefinitionId)

          getUserProfile({
            variables: {
              Type: PROFILE_TYPE.STUDENT,
              Id: StudentEmail,
              Key: 'Email',
            },
          })
        }
      }
    }, EAP_POLLING_INTERVAL_MS)

    return () => {
      clearInterval(timeout)
    }
  }, [code])

  /**
   * Log the event
   */
  React.useEffect(() => {
    const messageContext = {
      code,
      enrollment: autoEnrollmentsByCode.get(code),
    }

    if (productDefinitionId) {
      __DEV__ && console.log('Enrollment Auto Provision Modal Opened', messageContext)
      window?.DD_LOGS?.logger?.info('Enrollment Auto Provision Modal Opened', messageContext)
    }
  }, [code, productDefinitionId])

  return (
    <Overrides hideCloseButton={hideCloseButton}>
      <Modal open={props.open} onClose={onClose}>
        {raiseCase ? (
          <RaiseACaseModalContent
            {...props}
            title={Message}
            description={Code}
            img={Image}
            onClose={() => setRaiseCase(false)}
          />
        ) : (
          <>
            {showLoaderModal ? (
              <LoadingContent {...props} title={Message} description={Code} img={Image} />
            ) : (
              <WarningModalContent {...props} title={Message} description={Code} img={Image} onProceed={onProceed} />
            )}
          </>
        )}
      </Modal>
    </Overrides>
  )
}

export default injectIntl(EAPModal)
